import React, { useId } from "react";
import {
    CircleBadge,
    HStack,
    IconButton,
    Menu,
    Text,
    Tooltip,
} from "@commonsku/design-system";
import { Route } from "lucide-react";
import {
    LuGraduationCap,
    LuMegaphone,
    LuMessageSquare,
    LuMonitorDot,
} from "react-icons/lu";
import { Userpilot } from "userpilot";
import config from "../../config";
import { useHasCapabilities, useHasUserFlag } from "../../hooks";
import withReducers from "../../store/withReducers";
import communityEventsSlice from "../community-events/communityEventsSlice";
import { useUpcomingWebinar } from "./useUpcomingWebinar";

const USERPILOT_RESOURCE_CENTER_TOKEN = "resource_center:Kc_mOv7Ij0";

const openCommunityEvents = () => {
    window.open("/community_events.php", "_blank", "noopener,noreferrer");
};

const ResourcesMenu = () => {
    const hasSkummunityFlag = useHasUserFlag(["SKUMMUNITY_ENABLED"], true);
    const hasSkummunityCapability = useHasCapabilities(
        ["ACCESS-SKUMMUNITY-FORUM"],
        true,
    );
    const showSkummunity = hasSkummunityFlag || hasSkummunityCapability;
    const { showUrgencyBadge, webinarStatusLabel } = useUpcomingWebinar();

    const triggerId = useId();

    return (
        <Menu.Root
            variant="subtle"
            ids={{ trigger: triggerId }}
            positioning={{
                placement: "bottom-end",
                strategy: "fixed",
            }}
        >
            <Tooltip.Root
                ids={{ trigger: triggerId }}
                positioning={{
                    placement: "bottom-end",
                    strategy: "fixed",
                }}
            >
                <Tooltip.Trigger asChild>
                    <Menu.Trigger asChild>
                        <CircleBadge
                            count={showUrgencyBadge ? "!" : 0}
                            color="brand"
                            size="small"
                            overflowCount={99}
                        >
                            <IconButton
                                label="Resources & Community"
                                size="sm"
                                variant="outline"
                                colorPalette="neutral"
                                rounded="md"
                            >
                                <LuGraduationCap />
                            </IconButton>
                        </CircleBadge>
                    </Menu.Trigger>
                </Tooltip.Trigger>
                <Tooltip.Positioner>
                    <Tooltip.Content>Resources & Community</Tooltip.Content>
                </Tooltip.Positioner>
            </Tooltip.Root>
            <Menu.Positioner>
                <Menu.Content padding="1" gap="1" width="72" minW="32">
                    <Menu.Item value="webinars" onClick={openCommunityEvents}>
                        <Menu.ItemText>
                            <HStack gap="1.5" width="full" justifyContent="space-between">
                                <HStack gap="1.5">
                                    <LuMonitorDot size={16} />
                                    <Text fontSize="xs" marginBottom="0">
                                        Webinars
                                    </Text>
                                </HStack>
                                {webinarStatusLabel ? (
                                    <Text fontSize="xs" marginBottom="0">
                                        {webinarStatusLabel.label}
                                        {webinarStatusLabel.time ? (
                                            <>
                                                {" "}
                                                <Text
                                                    as="span"
                                                    fontSize="xs"
                                                    fontWeight="600"
                                                    marginBottom="0"
                                                >
                                                    {webinarStatusLabel.time}
                                                </Text>
                                            </>
                                        ) : null}
                                    </Text>
                                ) : null}
                            </HStack>
                        </Menu.ItemText>
                    </Menu.Item>
                    {/*
                     * Keep this resource center in Icon mode during beta so
                     * legacy-navbar users retain beacon access. After the new
                     * navbar is GA and legacy access has ended, switch the
                     * center to Only Manually in UserPilot, publish it, and
                     * verify the beacon is gone and this item opens the center
                     * in production. If the center is replaced, update the
                     * token above.
                     */}
                    <Menu.Item
                        id="userpilot-product-walkthroughs-trigger"
                        value="product-walkthroughs"
                        onClick={() =>
                            Userpilot.trigger(USERPILOT_RESOURCE_CENTER_TOKEN)
                        }
                    >
                        <Menu.ItemText>
                            <HStack gap="1.5">
                                <Route size={16} />
                                <Text fontSize="xs" marginBottom="0">
                                    Product Walkthroughs
                                </Text>
                            </HStack>
                        </Menu.ItemText>
                    </Menu.Item>
                    {showSkummunity && (
                        <Menu.Item
                            value="skummunity-forum"
                            onClick={() =>
                                window.open(
                                    `${config.skummunity_base_url}/session/sso?return_path=${config.skummunity_return_path}`,
                                    "_blank",
                                    "noopener,noreferrer",
                                )
                            }
                        >
                            <Menu.ItemText>
                                <HStack gap="1.5">
                                    <LuMegaphone size={16} />
                                    <Text fontSize="xs" marginBottom="0">
                                        skummunity Forum
                                    </Text>
                                </HStack>
                            </Menu.ItemText>
                        </Menu.Item>
                    )}
                    <Menu.Item
                        value="support-chat"
                        onClick={() => {
                            window.location.href = "/chat.php";
                        }}
                    >
                        <Menu.ItemText>
                            <HStack gap="1.5">
                                <LuMessageSquare size={16} />
                                <Text fontSize="xs" marginBottom="0">
                                    Support Chat
                                </Text>
                            </HStack>
                        </Menu.ItemText>
                    </Menu.Item>
                </Menu.Content>
            </Menu.Positioner>
        </Menu.Root>
    );
};

export default withReducers(ResourcesMenu, {
    community_events: communityEventsSlice.reducer,
});
